import { select, call, put } from 'redux-saga/effects'
import Logger from '../../../services/logger.service'
import {
  GlobalDataImportService,
  GlobalDataInfoList,
  IGlobalDataPaginationResponse,
  IGlobalDataRequest,
  IGlobalDataResponse,
} from '../services/globalDataImport.service'
import { getAuthenticationJwtSelector } from '../../login/selectors'
import { GlobalDataService } from '../../../services/globalData.service'
import { IndexableType } from 'dexie'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { isGlobalDataLoading, onGlobalDataImportSuccess } from '../store/globalData.actions'
import { globalDataMetadata, GlobalDataMetadataService } from '../../../services/globalDataMetadata.service'
import { setLocationsData } from './setLocationsData.saga'

export function* setGlobalData() {
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  try {
    const globalDataImportService = new GlobalDataImportService(authenticationJwt as string)
    const globalDataMetadataService = new GlobalDataMetadataService()

    yield put(isGlobalDataLoading(true))

    // Get all the metadata docs and build pagination request with last updated time stamp
    const globalDataMetadataAll: globalDataMetadata = yield call([
      globalDataMetadataService,
      globalDataMetadataService.getAllDoc,
    ])
    const globalDataReq: GlobalDataInfoList = globalDataMetadataAll.map((doc) => {
      return { id: doc.id, lastUpdatedTime: doc.lastUpdatedTime }
    })
    const paginationRequest: IGlobalDataRequest = {
      globalData: globalDataReq,
    }
    const globalDataPaginationRepsonse: IGlobalDataPaginationResponse = yield call(
      [globalDataImportService, globalDataImportService.getGlobalDataPagination],
      paginationRequest
    )

    // The pagination response has all the docIds that are not available or do not match the last updated time stamp grouped by pages
    // Build a resquest per page get get respective docData
    // Pages are built on server based on size
    if (globalDataPaginationRepsonse.data) {
      for (const globalDataDoc of globalDataPaginationRepsonse.data) {
        const globalDataReq = globalDataDoc.docIds?.map((id) => {
          return { id, lastUpdatedTime: '' }
        })
        const request: IGlobalDataRequest = {
          globalData: globalDataReq,
        }
        const globalDataRepsonse: IGlobalDataResponse = yield call(
          [globalDataImportService, globalDataImportService.getGlobalData],
          request
        )

        const { data } = globalDataRepsonse
        if (data && data.length > 0) {
          for (const globalDataResponseDoc of data) {
            if (globalDataResponseDoc.id && globalDataResponseDoc.docData) {
              // Save the doc data to respective tables based on docGroup
              // After save is successful add a record to metadata store for next pagination request
              if (globalDataResponseDoc.docGroup === 'locations') {
                yield call(setLocationsData, globalDataResponseDoc)
                continue
              }
              const globalDataService = new GlobalDataService()
              if (globalDataResponseDoc.docData) {
                let globalData = null
                if (
                  globalDataResponseDoc.docGroup === 'validations' ||
                  globalDataResponseDoc.docGroup === 'configurations'
                ) {
                  globalData = JSON.parse(globalDataResponseDoc.docData)
                } else {
                  globalData = { id: globalDataResponseDoc.id, js: globalDataResponseDoc.docData }
                }
                const crudResponse: IndexableType = yield call(
                  [globalDataService, globalDataService.createOrUpdateDocument],
                  globalData
                )
                if (crudResponse) {
                  // Add this to metadata table only after the CRUD is success
                  yield call(
                    [globalDataMetadataService, globalDataMetadataService.createOrUpdate],
                    globalDataResponseDoc
                  )
                }
              }
            }
          }
        }
      }
    }
  } catch (e: any) {
    let cause = ''
    if (e.messages && e.messages.length) {
      const index = e.messages.findIndex((x: any) => x.status === e.status)
      cause = e.messages[index].message
      yield put(handleApiError(e))
    }
    Logger.error(`Error retrieving global data', ${e}, cause ${cause}`)
  } finally {
    yield put(isGlobalDataLoading(false))
    yield put(onGlobalDataImportSuccess())
  }
}
